import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Truck, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

/**
 * Upgrade an existing customer/admin account to a driver account.
 * Reuses the user's auth token — no email/password needed.
 */
export default function BecomeDriver() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    address: "", city: "", state: "PA", zip_code: "",
    drivers_license: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_color: "", license_plate: "",
  });

  if (!isAuthenticated) {
    navigate("/login?redirect=/become-driver");
    return null;
  }
  if (user?.role === "driver") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5">
        <Card className="bg-[#121212] border-white/5 max-w-md">
          <CardContent className="p-8 text-center space-y-4">
            <Truck className="w-12 h-12 text-[#4D88FF] mx-auto" />
            <p className="text-white text-lg font-bold">You're already a driver</p>
            <Button onClick={() => navigate("/driver")} className="bg-[#0047FF] hover:bg-[#003BE0] text-white" data-testid="go-to-driver">
              Open driver dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    const required = ["address", "city", "state", "zip_code", "vehicle_make", "vehicle_model", "vehicle_year", "license_plate"];
    for (const k of required) {
      if (!form[k]?.toString().trim()) { toast.error(`Please fill in ${k.replace(/_/g, " ")}`); return; }
    }
    const yr = parseInt(form.vehicle_year, 10);
    if (!yr || yr < 2016) { toast.error("Vehicle must be 2016 or newer"); return; }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/me/become-driver`, form);
      // Swap the token so the rest of the session is authenticated as the new role
      if (data?.token) {
        localStorage.setItem("linklite_token", data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      }
      toast.success("Driver capabilities added!");
      // Hard reload so AuthContext picks up the new role + nav updates
      setTimeout(() => { window.location.href = "/driver/profile"; }, 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-5">
      <Toaster position="top-center" />
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/account")} className="text-zinc-400 hover:text-white" data-testid="bd-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, system-ui" }}>Become a Driver</h1>
            <p className="text-sm text-zinc-500">Add driver capabilities to <strong className="text-white/80">{user?.email}</strong></p>
          </div>
        </div>

        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 mb-5">
          <p className="text-xs text-amber-300">
            <strong>Beta requirements:</strong> Vehicle must be 2016 or newer. Background check & document upload required after this step. Pickups must originate within 25 mi of Reading, PA.
          </p>
        </div>

        <Card className="bg-[#121212] border-white/5">
          <CardContent className="p-5 space-y-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Address</p>
            <div>
              <Label className="text-zinc-400 text-xs">Street Address *</Label>
              <Input value={form.address} onChange={(e) => update("address", e.target.value)} className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">City *</Label>
                <Input value={form.city} onChange={(e) => update("city", e.target.value)} className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-city" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">State *</Label>
                <Input value={form.state} onChange={(e) => update("state", e.target.value)} maxLength={2} className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-state" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">ZIP *</Label>
                <Input value={form.zip_code} onChange={(e) => update("zip_code", e.target.value)} className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-zip" />
              </div>
            </div>

            <div className="pt-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold flex items-center gap-2"><Car className="w-3 h-3" /> Vehicle</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Make *</Label>
                <Input value={form.vehicle_make} onChange={(e) => update("vehicle_make", e.target.value)} placeholder="Honda" className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-make" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Model *</Label>
                <Input value={form.vehicle_model} onChange={(e) => update("vehicle_model", e.target.value)} placeholder="Civic" className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-model" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Year *</Label>
                <Input type="number" min="2016" max="2027" value={form.vehicle_year} onChange={(e) => update("vehicle_year", e.target.value)} placeholder="2022" className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-year" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Color</Label>
                <Input value={form.vehicle_color} onChange={(e) => update("vehicle_color", e.target.value)} placeholder="Black" className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-color" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Plate *</Label>
                <Input value={form.license_plate} onChange={(e) => update("license_plate", e.target.value)} placeholder="ABC1234" className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-plate" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Driver's License #</Label>
              <Input value={form.drivers_license} onChange={(e) => update("drivers_license", e.target.value)} placeholder="(optional now — required before approval)" className="bg-zinc-900 border-white/10 text-white mt-1" data-testid="bd-dl" />
            </div>

            <Button onClick={submit} disabled={loading} className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl border-0 mt-2" data-testid="bd-submit">
              {loading ? "Adding driver capabilities…" : "Become a Driver"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
